import { parseISO } from "date-fns";

/**
 * Parseia string ISO ou Date de forma segura. Evita TypeError quando valor é undefined/null.
 * @param value - String de data (YYYY-MM-DD ou ISO), Date, ou undefined
 * @returns Date ou null se inválido
 */
export function safeParseISO(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const d = parseISO(value);
    return d && !Number.isNaN(d.getTime()) ? d : null;
  } catch {
    return null;
  }
}

/**
 * Split seguro - evita TypeError quando valor é undefined/null.
 * Root cause: API pode retornar campos vazios; .split() em undefined lança TypeError.
 * @param str - String a ser dividida (pode ser undefined/null)
 * @param separator - Separador (padrão: ',')
 * @returns Array de strings (nunca undefined)
 */
export function safeSplit(str: unknown, separator: string | RegExp = ','): string[] {
  if (str == null) return [];
  if (typeof str !== 'string') return [];
  try {
    const result = str.split(separator);
    return Array.isArray(result) ? result : [];
  } catch {
    return [];
  }
}

export function createPageUrl(pageName: string) {
  if (pageName == null || typeof pageName !== 'string') return '/';
  try {
    const s = String(pageName).trim();
    return s ? '/' + s.toLowerCase().replace(/ /g, '-') : '/';
  } catch {
    return '/';
  }
}

/** Query para abrir Resultados na aba Dados Gerais com a barra lateral recolhida (usa `nav=collapsed` no Layout). */
export const RESULTADOS_ABA_GERAIS_NAV_RECOLHIDA = 'tab=gerais&nav=collapsed';

/** URL de Resultados para um edital, com aba gerais e navegação recolhida (ex.: nova aba desde Análise ou Kanban). */
export function createResultadosEditalDeepLink(editalId: string | number) {
  const id = encodeURIComponent(String(editalId));
  return `${createPageUrl('Resultados')}?id=${id}&${RESULTADOS_ABA_GERAIS_NAV_RECOLHIDA}`;
}

/** Abre uma rota da SPA em nova aba (mesma origem). */
export function openPageInNewTab(pathAndQuery: string) {
  if (typeof window === 'undefined') return;
  const raw = pathAndQuery == null ? '' : String(pathAndQuery).trim();
  if (!raw) return;
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  window.open(new URL(path, window.location.origin).href, '_blank', 'noopener,noreferrer');
}

/**
 * Valor seguro para input type="text" - evita "[object Object]" quando IA retorna objeto.
 * Extrai string de objetos com .name, .text, .value, .label ou primeira propriedade string.
 */
export function safeStringForInput(val: unknown): string {
  if (val == null) return '';
  if (typeof val === 'string') return val.trim();
  if (typeof val === 'number') return String(val);
  if (typeof val === 'object') {
    const o = val as Record<string, unknown>;
    const s = o.name ?? o.text ?? o.value ?? o.label ?? o.descricao ?? o.titulo;
    if (typeof s === 'string') return s.trim();
    const firstStr = Object.values(o).find((v) => typeof v === 'string');
    return typeof firstStr === 'string' ? firstStr.trim() : '';
  }
  return String(val).trim();
}

/**
 * Valor seguro para input type="number" - extrai número de "R$ 10.323.971,52", "R$ 40.471,20 (quarenta mil...)" etc.
 * @param val - Valor bruto (string, number ou null/undefined)
 * @returns Número para exibição ou '' se inválido
 */
export function safeValorForInput(val: unknown): number | '' {
  if (val == null || val === '') return '';
  if (typeof val === 'number' && !Number.isNaN(val)) return val;
  let s = String(val).trim();
  if (!s) return '';
  // Remove prefixos comuns: R$, US$, etc.
  s = s.replace(/^[RrUuSs$€£\s]+/, '').trim();
  const numPart = s.match(/[\d.,]+/);
  if (!numPart) return '';
  const raw = numPart[0];
  // BR: 10.323.971,52 (ponto=milhar, vírgula=decimal) | US: 10,323,971.52
  const clean = /,\d{1,2}$/.test(raw)
    ? raw.replace(/\./g, '').replace(',', '.')
    : raw.replace(/,/g, '');
  const num = parseFloat(clean);
  return Number.isNaN(num) ? '' : num;
}

/**
 * Valor seguro para input type="date" - só aceita yyyy-MM-dd ou ''.
 * Converte DD/MM/YYYY, "Mês de YYYY", etc.
 * @param val - Valor bruto
 * @param useLast - Se true, pega a última data quando há várias no texto
 */
export function safeDataForInput(val: unknown, useLast = false): string {
  try {
  if (val == null || val === '') return '';
  if (typeof val === 'object' && !(val instanceof Date)) {
    const o = val as Record<string, unknown>;
    val = o.value ?? o.date ?? o.data ?? o.start ?? o.end ?? '';
    if (val == null || val === '') return '';
  }
  let s = (typeof val === 'string' ? val : String(val)).trim();
  if (!s) return '';
  // Se começa com data DD/MM/YYYY, pegar só a data antes de " (" ou " -"
  const beforeParen = s.split(/\s*[\(\-]/)[0]?.trim();
  if (beforeParen && /^\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4}$/.test(beforeParen)) {
    s = beforeParen;
  }
  if (s.length > 20 && !/\d{4}/.test(s)) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.split('T')[0] || s;
  // Busca YYYY-MM-DD em qualquer parte: "...Nota de Empenho de 2026-01-28)" ou "referência: 2026-01-28"
  const isoMatch = s.match(/\d{4}-\d{2}-\d{2}/g);
  if (isoMatch && isoMatch.length > 0) {
    const chosen = useLast ? isoMatch[isoMatch.length - 1] : isoMatch[0];
    if (chosen && typeof chosen === 'string') return chosen.split('T')[0] || chosen;
  }
  // Busca DD/MM/YYYY em qualquer parte: "Data de assinatura... em 28/01/2026" ou "12/12/2026 (Não poderá..."
  const ddmmyyyy = s.match(/(\d{1,2})\s*[\/\-\.]\s*(\d{1,2})\s*[\/\-\.]\s*(\d{4})/g);
  if (ddmmyyyy && ddmmyyyy.length > 0) {
    const chosen = useLast ? ddmmyyyy[ddmmyyyy.length - 1] : ddmmyyyy[0];
    if (chosen && typeof chosen === 'string') {
      const m = chosen.match(/(\d{1,2})\s*[\/\-\.]\s*(\d{1,2})\s*[\/\-\.]\s*(\d{4})/);
      if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
    }
  }
  const meses: Record<string, string> = {
    janeiro: '01', fevereiro: '02', março: '03', marco: '03', abril: '04',
    maio: '05', junho: '06', julho: '07', agosto: '08', setembro: '09',
    outubro: '10', novembro: '11', dezembro: '12',
  };
  const mesAno = s.match(/(\w+)\s*(?:de|\/)\s*(\d{4})/i);
  if (mesAno && meses[mesAno[1].toLowerCase()]) {
    return `${mesAno[2]}-${meses[mesAno[1].toLowerCase()]}-01`;
  }
  return '';
  } catch {
    return '';
  }
}